import { useMemo, useRef, useContext } from 'react';
import { QuestionEntry } from '@wix/ambassador-faq-question-entry-v2-question-entry/types';
import { usePaginationQuestions } from './usePaginationQuestions';
import { usePaginationSearchQuestions } from './usePaginationSearchQuestions';
import { AppContext } from '../components/FaqOoi/Widget/SettingsProvider/SettingsProvider';

export function areQuestionsEqual(
  a: QuestionEntry[] | null | undefined,
  b: QuestionEntry[] | null | undefined,
): boolean {
  if (a === b) {
    return true;
  }
  if (!a || !b || a.length !== b.length) {
    return false;
  }
  return a.every(
    (entry, i) =>
      entry.id === b[i]?.id && entry.updatedDate === b[i]?.updatedDate,
  );
}

/**
 * Returns a stable questions list reference when content is unchanged.
 * Derives from initialQuestions instead of syncing in an effect to avoid an
 * extra re-render when data arrives after first paint (multiple renders on start).
 */
export const useQuestions = (initialQuestions: QuestionEntry[]) => {
  const { searchTerm } = useContext(AppContext);
  const lastListRef = useRef<QuestionEntry[]>(initialQuestions ?? []);

  const questionsList = useMemo(() => {
    const next = initialQuestions ?? [];
    if (areQuestionsEqual(lastListRef.current, next)) {
      return lastListRef.current;
    }
    lastListRef.current = next;
    return next;
  }, [initialQuestions]);

  const bottomRef = useRef<HTMLDivElement | null>(null);

  // Manage load more logic only when not searching
  usePaginationQuestions({
    bottomRef,
    disabled: !!searchTerm,
  });

  // Manage load more logic when searching
  usePaginationSearchQuestions({
    bottomRef,
    disabled: !searchTerm,
  });

  return { questionsList, bottomRef };
};
